// src/redux/api/goodies.api.ts
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc, 
  query, 
  where,
  Timestamp
} from "firebase/firestore";
import { DB , uploadFileFirebase, uploadMultipleFilesFirebase } from "src/contexts/FirebaseContext";
import { Goodie, GoodieFormValues } from "src/@types/goodies";


export const GoodiesApi = createApi({
  reducerPath: "goodiesApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["Goodie", "EventGoodies"],
  endpoints: (builder) => ({
    // Get all goodies for an event
    fetchEventGoodies: builder.query<Goodie[], string>({
      async queryFn(eventId) {
        try {
          const goodiesRef = collection(DB, "events", eventId, "goodies");
          const goodiesSnapshot = await getDocs(goodiesRef);
          const goodiesList = goodiesSnapshot.docs.map((doc) => {
            const data = doc.data();
            // Calculate total stock if has sizes
            let totalStock = data.stock || 0;
            if (data.hasSizes && data.sizes) {
              totalStock = data.sizes.reduce((sum: number, size: any) => sum + (size.stock || 0), 0);
            }
            
            return {
              id: doc.id,
              ...data,
              stock: totalStock,
            };
          }) as Goodie[];
          
          return { data: goodiesList };
        } catch (error: any) {
          console.error("Error fetching goodies:", error);
          return { 
            error: {
              status: error?.code || "UNKNOWN_ERROR",
              data: error?.message || "An error occurred while fetching goodies"
            }
          };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Goodie" as const, id })),
              { type: "EventGoodies", id: "LIST" },
            ]
          : [{ type: "EventGoodies", id: "LIST" }],
    }),
    
    // Get a single goodie
    fetchGoodie: builder.query<Goodie, { eventId: string; goodieId: string }>({
      async queryFn({ eventId, goodieId }) {
        try {
          const goodieDoc = await getDoc(doc(DB, "events", eventId, "goodies", goodieId));
          
          if (!goodieDoc.exists()) {
            return { error: { status: "NOT_FOUND", data: "Goodie not found" } };
          }
          
          const data = goodieDoc.data();
          // Calculate total stock if has sizes
          let totalStock = data.stock || 0;
          if (data.hasSizes && data.sizes) {
            totalStock = data.sizes.reduce((sum: number, size: any) => sum + (size.stock || 0), 0);
          }
          
          return {
            data: { 
              id: goodieDoc.id, 
              ...data,
              stock: totalStock,
            } as Goodie,
          };
        } catch (error: any) {
          return {
            error: {
              status: error?.code || "UNKNOWN_ERROR",
              data: error?.message || "An error occurred while fetching the goodie"
            }
          };
        }
      },
      providesTags: (result, error, { goodieId }) => [{ type: "Goodie", id: goodieId }],
    }),
    
    // Add a new goodie
    addGoodie: builder.mutation<{ id: string }, { eventId: string; goodie: GoodieFormValues; image?: File; images?: File[] }>({
      async queryFn({ eventId, goodie, image, images }) {
        try {
          let imageUrl = goodie.image;
          let imageUrls: string[] = [];

          // Upload multiple images if provided
          if (images && images.length > 0) {
            const uploadedUrls = await uploadMultipleFilesFirebase(images, `events/${eventId}/goodies/`);
            imageUrls = uploadedUrls;

            // Set the first image as the primary image for backward compatibility
            if (imageUrls.length > 0) {
              imageUrl = imageUrls[0];
            }
          } else if (image) {
            // Fallback to single image upload
            imageUrl = await uploadFileFirebase(image, `events/${eventId}/goodies/`);
            imageUrls = [imageUrl];
          }

          // Process sizes if present
          let processedSizes = goodie.sizes;
          if (goodie.hasSizes && goodie.sizes) {
            processedSizes = goodie.sizes.map((size, index) => ({
              ...size,
              id: size.id || `size_${index}_${Date.now()}`,
            }));
          }

          const newGoodie = {
            ...goodie,
            image: imageUrl,
            images: imageUrls.length > 0 ? imageUrls : undefined,
            eventId,
            sizes: processedSizes,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
          };

          const goodiesRef = collection(DB, "events", eventId, "goodies");
          const docRef = await addDoc(goodiesRef, newGoodie);

          return { data: { id: docRef.id } };
        } catch (error: any) {
          console.error("Error adding goodie:", error);
          return {
            error: {
              status: error?.code || "UNKNOWN_ERROR",
              data: error?.message || "An error occurred while adding the goodie"
            }
          };
        }
      },
      invalidatesTags: [{ type: "EventGoodies", id: "LIST" }],
    }),
    
    // Update a goodie
    updateGoodie: builder.mutation<
      void,
      { eventId: string; goodieId: string; goodie: Partial<GoodieFormValues>; image?: File; images?: (File | string)[] }
    >({
      async queryFn({ eventId, goodieId, goodie, image, images }) {
        try {
          let imageUrl = goodie.image;
          let imageUrls: string[] | undefined = undefined;

          // Upload multiple images if provided
          if (images && images.length > 0) {
            // Filter out string URLs (existing images) and only upload new Files
            const newFiles = images.filter(img => img instanceof File) as File[];
            const existingUrls = images.filter((img): img is string => typeof img === 'string');

            if (newFiles.length > 0) {
              const uploadedUrls = await uploadMultipleFilesFirebase(newFiles, `events/${eventId}/goodies/`);
              imageUrls = [...existingUrls, ...uploadedUrls];
            } else {
              imageUrls = existingUrls;
            }

            // Set the first image as the primary image for backward compatibility
            if (imageUrls.length > 0) {
              imageUrl = imageUrls[0];
            }
          } else if (image) {
            // Fallback to single image upload
            imageUrl = await uploadFileFirebase(image, `events/${eventId}/goodies/`);
            imageUrls = [imageUrl];
          }

          // Process sizes if present
          let processedSizes = goodie.sizes;
          if (goodie.hasSizes && goodie.sizes) {
            processedSizes = goodie.sizes.map((size, index) => ({
              ...size,
              id: size.id || `size_${index}_${Date.now()}`,
            }));
          }

          const updateData = {
            ...goodie,
            ...(imageUrl && { image: imageUrl }),
            ...(imageUrls && { images: imageUrls }),
            ...(processedSizes && { sizes: processedSizes }),
            updatedAt: Timestamp.now(),
          };

          const goodieRef = doc(DB, "events", eventId, "goodies", goodieId);
          await updateDoc(goodieRef, updateData);

          return { data: undefined };
        } catch (error: any) {
          console.error("Error updating goodie:", error);
          return {
            error: {
              status: error?.code || "UNKNOWN_ERROR",
              data: error?.message || "An error occurred while updating the goodie"
            }
          };
        }
      },
      invalidatesTags: (result, error, { goodieId }) => [
        { type: "Goodie", id: goodieId },
        { type: "EventGoodies", id: "LIST" },
      ],
    }),
    
    // Update goodie image
    updateGoodieImage: builder.mutation<
      string,
      { eventId: string; goodieId: string; image: File }
    >({
      async queryFn({ eventId, goodieId, image }) {
        try {
          const imageUrl = await uploadFileFirebase(image, `events/${eventId}/goodies/`);
          
          const goodieRef = doc(DB, "events", eventId, "goodies", goodieId);
          await updateDoc(goodieRef, {
            image: imageUrl,
            updatedAt: Timestamp.now(),
          });
          
          return { data: imageUrl };
        } catch (error: any) {
          console.error("Error updating goodie image:", error);
          return {
            error: {
              status: error?.code || "UNKNOWN_ERROR",
              data: error?.message || "An error occurred while updating the goodie image"
            }
          };
        }
      },
      invalidatesTags: (result, error, { goodieId }) => [{ type: "Goodie", id: goodieId }],
    }),
    
    // Delete a goodie
    deleteGoodie: builder.mutation<void, { eventId: string; goodieId: string }>({
      async queryFn({ eventId, goodieId }) {
        try {
          const goodieRef = doc(DB, "events", eventId, "goodies", goodieId);
          await deleteDoc(goodieRef);
          
          return { data: undefined };
        } catch (error: any) {
          console.error("Error deleting goodie:", error);
          return {
            error: {
              status: error?.code || "UNKNOWN_ERROR",
              data: error?.message || "An error occurred while deleting the goodie"
            }
          };
        }
      },
      invalidatesTags: (result, error, { goodieId }) => [
        { type: "Goodie", id: goodieId },
        { type: "EventGoodies", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useFetchEventGoodiesQuery,
  useFetchGoodieQuery,
  useAddGoodieMutation,
  useUpdateGoodieMutation,
  useUpdateGoodieImageMutation,
  useDeleteGoodieMutation,
} = GoodiesApi;